import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListAccountsResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-49 Companies.
 *
 * The company RECORD was reachable at /accounts/:id and nothing listed them:
 * the only way to a company was through a contact who happened to work there.
 * `listAccounts` had been published, paginated and agent-callable the whole
 * time, with no caller in the product.
 *
 * Paged by the server, unlike the other index screens added beside it. A
 * workspace has tens of audiences and thousands of companies, and narrowing
 * thousands in the browser would be a search box that quietly searched the
 * first page.
 */

type Row = ListAccountsResult['rows'][number];

function CompaniesListScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'name',
    direction: 'asc',
  });
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;

  const query = useQuery<ListAccountsResult, ApiError>({
    queryKey: ['accounts', { search, cursor }],
    queryFn: () =>
      api.listAccounts({
        query: {
          ...(search.trim() ? { search: search.trim() } : {}),
          ...(cursor ? { cursor } : {}),
          limit: 50,
        },
      }),
  });

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('list.column.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => row.name,
      },
      {
        key: 'industry',
        header: t('list.companies.industry'),
        cell: (row) => row.industry ?? '',
      },
      {
        key: 'contacts',
        header: t('list.companies.contacts'),
        align: 'end',
        /*
         * From the server's count, never from the rows on screen.
         *
         * A company's people are not in this response; a number derived from
         * what is here would be zero for every row and look like a company
         * nobody works at.
         */
        cell: (row) => (
          <Num align="end" className="text-ink">
            {formatCount(row.contactCount, format)}
          </Num>
        ),
      },
      {
        key: 'owner',
        header: t('list.companies.owner'),
        cell: (row) => row.ownerName ?? '',
        hideOnCard: true,
      },
    ],
    [t, format],
  );

  const rows = query.data?.rows ?? [];

  return (
    <RecordList<Row>
      title={t('list.companies.title')}
      icon="Buildings"
      rowId={(row) => row.accountId}
      recordHref={(row) => `/accounts/${row.accountId}`}
      columns={columns}
      page={
        query.data
          ? {
              rows,
              total: query.data.total,
              hasMore: query.data.hasMore,
              nextCursor: query.data.nextCursor,
            }
          : undefined
      }
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={(value) => {
        setSearch(value);
        // A different search is a different list, so the cursor names a page
        // of something else.
        setCursorStack([]);
      }}
      sort={sort}
      onSortChange={(field) => {
        setSort((current) => nextSort(current, field));
        setCursorStack([]);
      }}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={query.data?.pageSize ?? 50}
      onClearFilters={() => {
        setSearch('');
        setCursorStack([]);
      }}
      filtered={search.trim().length > 0}
      emptyTitle={t('list.companies.empty.title')}
      emptyBody={t('list.companies.empty.body')}
      emptyIllustration="Buildings"
    />
  );
}

function CompaniesEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Buildings"
      title={t('list.companies.empty.title')}
      body={t('list.companies.empty.body')}
      /*
       * Import, because that is how a first company actually arrives. There is
       * no create-company screen: companies come with their people, from a
       * file or from a lead being converted. Offering "New company" would be a
       * button with nowhere to go.
       */
      primary={{ label: t('action.import'), icon: 'Upload', href: '/setup/import' }}
    />
  );
}

function CompaniesLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading companies</span>
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function CompaniesError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Companies could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-49',
  route: '/accounts',
  surface: 'page',
  title: 'Companies list',
  features: ['F-028'],
  entitlement: 'F-028',
  reachedBy: [{ kind: 'nav', item: 'companies' }],
  components: ['DataTable', 'FilterChips'],
  elements: ['RecordList'],
  states: {
    empty: CompaniesEmpty,
    loading: CompaniesLoading,
    error: CompaniesError,
  },
  component: CompaniesListScreen,
});

export default screen;
